import type { SharedPageProps } from "@inertiajs/core";
import { createInertiaApp, router } from "@inertiajs/react";
import type { BrowserOptions } from "@sentry/react";
import * as Sentry from "@sentry/react";

import "../css/app.css";
import { resolveInertiaPage } from "./inertia-pages-resolver";
import { persistBrowserAppearance, projectBrowserAppearance } from "./lib/browser-appearance";
import { formatDocumentTitle } from "./lib/document-title";
import { createAppearanceActor } from "./lib/flows/appearance-machine";
import { clearReloadedImportScrollPosition } from "./lib/import-reload-scroll";
import type { Subscription } from "xstate";
import { mountInertiaApp } from "./lib/inertia-root";
import type { RootErrorHandler } from "./lib/inertia-root";
import { resolveSentryEnvironment, shouldInitializeSentry } from "./lib/sentry-environment";
import { guardSharedProps } from "./lib/shared-props";
import { ClientAppShell } from "./main";

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const sentryEnvironment = resolveSentryEnvironment(
  import.meta.env.VITE_SENTRY_ENVIRONMENT,
  import.meta.env.MODE,
);
const shouldReportToSentry = shouldInitializeSentry({
  dsn: sentryDsn,
  environment: sentryEnvironment,
  isProductionBuild: import.meta.env.PROD,
});
let productName = "Tally";

const failAppBoot = (error: unknown) => {
  queueMicrotask(() => {
    throw error instanceof Error
      ? error
      : new Error(`Failed to bootstrap the Inertia app: ${String(error)}`);
  });
};

const parseSampleRate = (sampleRate: string | undefined): number | undefined => {
  if (sampleRate === undefined || sampleRate === "") {
    return undefined;
  }

  return Number.parseFloat(sampleRate);
};

const syncSentryPageContext = (pageProps: Readonly<SharedPageProps>) => {
  if (pageProps.auth.user !== null) {
    Sentry.setUser({ id: String(pageProps.auth.user.id) });
  }

  if (pageProps.tenant === null) {
    return;
  }

  Sentry.setTag("tenant_id", pageProps.tenant.identity.id);

  if (pageProps.tenant.identity.slug !== null) {
    Sentry.setTag("tenant_slug", pageProps.tenant.identity.slug);
  }
};

if (shouldReportToSentry) {
  const tracesSampleRate = parseSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE);
  const replaysSessionSampleRate = parseSampleRate(
    import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
  );
  const replaysOnErrorSampleRate = parseSampleRate(
    import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
  );
  const hasReplayIntegration =
    (replaysSessionSampleRate !== undefined && replaysSessionSampleRate > 0) ||
    (replaysOnErrorSampleRate !== undefined && replaysOnErrorSampleRate > 0);

  const sentryOptions: BrowserOptions = {
    dsn: sentryDsn,
    enableLogs: true,
    environment: sentryEnvironment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.feedbackIntegration({
        colorScheme: "system",
      }),
      Sentry.consoleLoggingIntegration({
        levels: ["warn", "error"],
      }),
      ...(hasReplayIntegration ? [Sentry.replayIntegration()] : []),
    ],
    // oxlint-disable-next-line typescript/no-deprecated -- replacing this with per-category `dataCollection` changes the PII surface; decide that in the Sentry v11 upgrade, not in a lint fix.
    sendDefaultPii: true,
  };

  if (replaysOnErrorSampleRate !== undefined) {
    sentryOptions.replaysOnErrorSampleRate = replaysOnErrorSampleRate;
  }

  if (replaysSessionSampleRate !== undefined) {
    sentryOptions.replaysSessionSampleRate = replaysSessionSampleRate;
  }

  if (tracesSampleRate !== undefined) {
    sentryOptions.tracesSampleRate = tracesSampleRate;
  }

  Sentry.init(sentryOptions);

  router.on("navigate", (event) => {
    const { component, props } = event.detail.page;
    syncSentryPageContext(props);

    const activeSpan = Sentry.getActiveSpan();
    const rootSpan = activeSpan ? Sentry.getRootSpan(activeSpan) : undefined;

    if (rootSpan !== undefined) {
      Sentry.updateSpanName(rootSpan, component);
      rootSpan.setAttribute(Sentry.SEMANTIC_ATTRIBUTE_SENTRY_SOURCE, "route");
    }

    Sentry.getCurrentScope().setTransactionName(component);
  });
}

const reportSharedPropViolation = (error: Error) => {
  if (shouldReportToSentry) {
    Sentry.captureException(error);
  }
};

// Every page the client receives crosses this seam: `setup` for the page the
// document was served with, `navigate` for each one after it. Inertia merges a
// partial reload into the props it already holds before firing `navigate`, so
// the page read here is always the complete envelope.
router.on("navigate", (event) => {
  guardSharedProps(event.detail.page.props, reportSharedPropViolation);
});

clearReloadedImportScrollPosition();

try {
  await createInertiaApp({
    progress: false,
    resolve: resolveInertiaPage,
    setup({ el, App, props }) {
      if (el === null) {
        throw new Error("Inertia app root element not found.");
      }

      guardSharedProps(props.initialPage.props, reportSharedPropViolation);

      if (shouldReportToSentry) {
        syncSentryPageContext(props.initialPage.props);
      }

      const { productName: initialProductName } = props.initialPage.props;
      productName = initialProductName;

      const appearanceActor = createAppearanceActor({
        initialAppearance: props.initialPage.props.appearance,
      });
      const appearanceActorSubscriptions: Subscription[] = [];
      const appearanceActorLifecycle: {
        booting: boolean;
        disposed: boolean;
        error: Error | null;
      } = {
        booting: true,
        disposed: false,
        error: null,
      };
      const disposeAppearanceActor = (): void => {
        if (appearanceActorLifecycle.disposed) {
          return;
        }

        appearanceActorLifecycle.disposed = true;

        for (const subscription of appearanceActorSubscriptions) {
          subscription.unsubscribe();
        }

        appearanceActor.stop();
      };
      const reportAppearanceFailure = (error: unknown): void => {
        appearanceActorLifecycle.error =
          error instanceof Error ? error : new Error(`Appearance actor failed: ${String(error)}`);

        if (!appearanceActorLifecycle.booting) {
          disposeAppearanceActor();
          failAppBoot(appearanceActorLifecycle.error);
        }
      };

      appearanceActorSubscriptions.push(
        appearanceActor.subscribe({ error: reportAppearanceFailure }),
      );
      appearanceActorSubscriptions.push(
        appearanceActor.on("APPEARANCE_PROJECTION_REQUESTED", (event) => {
          try {
            projectBrowserAppearance(event.appearance, event.systemPrefersDark);
          } catch (error) {
            reportAppearanceFailure(error);
          }
        }),
      );
      appearanceActorSubscriptions.push(
        appearanceActor.on("APPEARANCE_PERSISTENCE_REQUESTED", (event) => {
          try {
            persistBrowserAppearance(event.appearance);
          } catch (error) {
            reportAppearanceFailure(error);
          }
        }),
      );

      try {
        appearanceActor.start();
        appearanceActor.send({ type: "CLIENT_MOUNTED" });

        if (appearanceActorLifecycle.error !== null) {
          throw appearanceActorLifecycle.error;
        }

        appearanceActorLifecycle.booting = false;
        import.meta.hot?.dispose(disposeAppearanceActor);

        const app = (
          <ClientAppShell appearanceActor={appearanceActor}>
            <App {...props} />
          </ClientAppShell>
        );
        const sentryErrorHandler = shouldReportToSentry ? Sentry.reactErrorHandler() : undefined;

        const handleReactError: RootErrorHandler | null =
          sentryErrorHandler === undefined
            ? null
            : (error, errorInfo) => {
                sentryErrorHandler(error, {
                  componentStack: errorInfo.componentStack ?? "",
                });
              };

        mountInertiaApp({
          app,
          el,
          onReactError: handleReactError,
        });
      } catch (error) {
        disposeAppearanceActor();

        throw error;
      }
    },
    title: (title) => formatDocumentTitle(title, productName),
  });
} catch (error) {
  failAppBoot(error);
}
